import { useEffect, useState } from "react";
import axios from "axios";
import { Gauge, Server, Clock, FlaskConical, AlertTriangle, Flame } from "lucide-react";
import { WarmupBadge } from "../Settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduleStep({ data, onChange }) {
  const [providers, setProviders] = useState([]);
  const [domains, setDomains] = useState([]);
  const maxPerHour = data.maxPerHour || 100;
  const provider = data.provider || "auto";
  const sendMode = data.sendMode || "now";
  const isDryRun = !!data.isDryRun;

  useEffect(() => {
    axios.get(`${API}/v1/settings/providers`).then((r) => setProviders(r.data.filter((p) => p.isActive)));
    axios.get(`${API}/v1/settings/domains`).then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  const senderDomain = domains.find((d) => d.domain === (data.fromDomain || "").toLowerCase());
  const warmup = senderDomain?.warmup;

  const selected = providers.find((p) => p.type === provider);
  const dailyCap = selected ? selected.dailyLimit : Math.max(...providers.map((p) => p.dailyLimit), 50000);
  const overCap = maxPerHour * 24 > dailyCap;

  const set = (patch) => onChange(patch);

  return (
    <div data-testid="schedule-step">
      <div className="panel"><div className="p-head"><Gauge size={15} /><h3>Sending speed</h3></div>
        <div className="p-body">
          <div className="slider-row">
            <input type="range" min={10} max={500} step={10} value={maxPerHour} data-testid="speed-slider" onChange={(e) => set({ maxPerHour: Number(e.target.value) })} className="slider" />
            <div className="slider-val" data-testid="speed-value">{maxPerHour}<span>/hour</span></div>
          </div>
          {overCap && <div className="warn-inline" data-testid="speed-warning"><AlertTriangle size={13} /> {maxPerHour}/hr projects to {(maxPerHour * 24).toLocaleString()}/day, above the provider daily cap of {dailyCap.toLocaleString()}.</div>}
          {warmup && warmup.warming && (
            <div className="warmup-note" data-testid="schedule-warmup">
              <Flame size={13} />
              <span>Sending from <b>{senderDomain.domain}</b> — <WarmupBadge warmup={warmup} /></span>
              <p className="note" style={{ margin: "6px 0 0" }}>This domain is warming up. Today's cap is {warmup.cap.toLocaleString()} emails; anything over that auto-defers to the next day so your sender reputation stays clean.</p>
            </div>
          )}
        </div>
      </div>

      <div className="cols" style={{ marginTop: 16 }}>
        <div className="panel"><div className="p-head"><Server size={15} /><h3>Provider routing</h3></div>
          <div className="p-body">
            <div className="field"><label>Route via</label>
              <select data-testid="provider-select" value={provider} onChange={(e) => set({ provider: e.target.value })}>
                <option value="auto">Auto-Route (Recommended)</option>
                {providers.map((p) => <option key={p.id} value={p.type}>{p.type} — {p.name}</option>)}
              </select>
            </div>
            <p className="note">Auto-Route picks the healthiest provider by priority. Forcing a provider overrides that.</p>
          </div>
        </div>

        <div className="panel"><div className="p-head"><Clock size={15} /><h3>Start time</h3></div>
          <div className="p-body">
            <label className="radio" data-testid="send-now-radio"><input type="radio" checked={sendMode === "now"} onChange={() => set({ sendMode: "now" })} /> Send immediately</label>
            <label className="radio" data-testid="send-later-radio"><input type="radio" checked={sendMode === "later"} onChange={() => set({ sendMode: "later" })} /> Schedule for later</label>
            {sendMode === "later" && <div className="field" style={{ marginTop: 8 }}><input type="datetime-local" data-testid="schedule-datetime" value={data.scheduledAt || ""} onChange={(e) => set({ scheduledAt: e.target.value })} /></div>}
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}><div className="p-head"><FlaskConical size={15} /><h3>Dry run</h3></div>
        <div className="p-body">
          <label className="chk" data-testid="dryrun-toggle"><input type="checkbox" checked={isDryRun} onChange={(e) => set({ isDryRun: e.target.checked })} /> Perform a dry run (process the queue but don't send real emails)</label>
          <p className="note">Dry runs exercise throttling, routing, and logging end-to-end and mark each message <b>DRY_RUN_SUCCESS</b> — perfect for a safe rehearsal.</p>
        </div>
      </div>
    </div>
  );
}
